import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { PrivacyProvider } from './context/PrivacyContext';
import { DocumentViewerProvider } from './context/DocumentViewerContext';
import LeadStore from './pages/LeadStore';
import LeadStoreAdmin from './pages/LeadStoreAdmin';
import { PaymentSuccess, PaymentCancel } from './pages/PaymentResult';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AdminSignup from './pages/AdminSignup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import MyTeam from './pages/MyTeam';
import Production from './pages/Production';
import Resources from './pages/Resources';
import PostSaleProcess from './pages/PostSaleProcess';
import CommissionCalculator from './pages/CommissionCalculator';
import ActivityTracker from './pages/ActivityTracker';
import Tracker from './pages/Tracker';
import Promotions from './pages/Promotions';
import ShowOptionsBuilder from './pages/ShowOptionsBuilder';
import ShowOptionsPresent from './pages/ShowOptionsPresent';
import Quiz from './pages/Quiz';
import AtlasQuestionLog from './pages/AtlasQuestionLog';
import Carriers from './pages/Carriers';
import CarrierDetail from './pages/CarrierDetail';
import Recruiting from './pages/Recruiting';
import Support from './pages/Support';
import Onboarding from './pages/Onboarding';
import OnboardingTracker from './pages/OnboardingTracker';
import Profile from './pages/Profile';
import BookOfBusiness from './pages/BookOfBusiness';
import AuditLog from './pages/AuditLog';
import CSVImport from './pages/CSVImport';
import MyTickets from './pages/MyTickets';
import AdminTickets from './pages/AdminTickets';
import EmailBlast from './pages/EmailBlast';
import Layout from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import '@/App.css';

// Paths a new agent can use before finishing the onboarding checklist
export const ONBOARDING_ALLOWED_PATHS = ['/onboarding', '/team', '/support', '/recruiting', '/profile'];

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // New-agent gate: until the checklist is done, only allow the whitelisted sections
  if (user.role !== 'admin' && user.onboarding_complete === false &&
      !ONBOARDING_ALLOWED_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return <Layout>{children}</Layout>;
}

// Same auth check, but renders WITHOUT the Layout chrome (sidebar/header).
// Used by the Show Options presentation view which is screen-shared full-bleed.
function BareProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
       <PrivacyProvider>
        <DocumentViewerProvider>
        <div className="App noise-texture">
          <AuthProvider>
            <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/signup/:token" element={<Signup />} />
              <Route path="/admin-signup/:token" element={<AdminSignup />} />
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/team" element={<ProtectedRoute><MyTeam /></ProtectedRoute>} />
              <Route path="/production" element={<ProtectedRoute><Production /></ProtectedRoute>} />
              <Route path="/book-of-business" element={<ProtectedRoute><BookOfBusiness /></ProtectedRoute>} />
              <Route path="/resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
              <Route path="/leads" element={<ProtectedRoute><LeadStore /></ProtectedRoute>} />
              <Route path="/leads/admin" element={<ProtectedRoute><LeadStoreAdmin /></ProtectedRoute>} />
              <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
              <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
              <Route path="/post-sale" element={<ProtectedRoute><PostSaleProcess /></ProtectedRoute>} />
              <Route path="/commission-calculator" element={<ProtectedRoute><CommissionCalculator /></ProtectedRoute>} />
              <Route path="/activity-tracker" element={<ProtectedRoute><ActivityTracker /></ProtectedRoute>} />
              <Route path="/tracker" element={<ProtectedRoute><Tracker /></ProtectedRoute>} />
              <Route path="/promotions" element={<ProtectedRoute><Promotions /></ProtectedRoute>} />
              <Route path="/bonuses" element={<Navigate to="/promotions" replace />} />
              <Route path="/show-options" element={<ProtectedRoute><ShowOptionsBuilder /></ProtectedRoute>} />
              <Route path="/show-options/present" element={<BareProtectedRoute><ShowOptionsPresent /></BareProtectedRoute>} />
              <Route path="/quiz" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
              <Route path="/atlas-question-log" element={<ProtectedRoute><AtlasQuestionLog /></ProtectedRoute>} />
              <Route path="/carriers" element={<ProtectedRoute><Carriers /></ProtectedRoute>} />
              <Route path="/carriers/:carrierId" element={<ProtectedRoute><CarrierDetail /></ProtectedRoute>} />
              <Route path="/recruiting" element={<ProtectedRoute><Recruiting /></ProtectedRoute>} />
              <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              <Route path="/admin/onboarding-tracker" element={<ProtectedRoute><OnboardingTracker /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/audit-log" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
              <Route path="/my-tickets" element={<ProtectedRoute><MyTickets /></ProtectedRoute>} />
              <Route path="/admin-tickets" element={<ProtectedRoute><AdminTickets /></ProtectedRoute>} />
              <Route path="/email-blast" element={<ProtectedRoute><EmailBlast /></ProtectedRoute>} />
              <Route path="/csv-import" element={<ProtectedRoute><CSVImport /></ProtectedRoute>} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </div>
        </DocumentViewerProvider>
       </PrivacyProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
